import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Trophy, TrendingUp, X, Users } from 'lucide-react';

const CONFETTI_EMOJIS = ['\u2B50', '\u2728', '\uD83C\uDF89', '\uD83C\uDFC6'];

const LeaderboardPanel = () => {
  const { user } = useAuth();
  // Startet immer geschlossen. Auto-Öffnen über Content (vor allem auf
  // Mobile) war störend. Geöffnet wird per FAB (Desktop) oder über das
  // 'leaderboard:open'-Event aus dem mobilen Burger-Menü.
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('winners');
  const [data, setData] = useState(null);
  const [friendsData, setFriendsData] = useState(null);
  const [confetti, setConfetti] = useState(null);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await api.leaderboard.get();
      setData(res.data);
    } catch {}
  }, []);

  const fetchFriendsLeaderboard = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.friends.leaderboard();
      setFriendsData(res.data);
    } catch {}
  }, [user]);

  useEffect(() => {
    fetchLeaderboard();
    if (user) fetchFriendsLeaderboard();
    const interval = setInterval(() => {
      fetchLeaderboard();
      if (user) fetchFriendsLeaderboard();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchLeaderboard, fetchFriendsLeaderboard, user]);

  // Öffnen aus dem mobilen Burger-Menü (window-Event statt Prop-Drilling,
  // gleiches Muster wie auth:logout / wallet:changed).
  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener('leaderboard:open', openHandler);
    return () => window.removeEventListener('leaderboard:open', openHandler);
  }, []);

  // Confetti every 30s
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => {
      const emoji = CONFETTI_EMOJIS[Math.floor(Math.random() * CONFETTI_EMOJIS.length)];
      setConfetti(emoji);
      setTimeout(() => setConfetti(null), 2000);
    }, 30000);
    return () => clearInterval(interval);
  }, [open]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <span className="lb-rank-icon" data-testid="rank-1-icon">{'\uD83D\uDC51'}</span>;
    if (rank === 2) return <span className="lb-rank-icon">{'\u2B50'}</span>;
    if (rank === 3) return <span className="lb-rank-icon">{'\uD83C\uDFC6'}</span>;
    return <span className="lb-rank-num">#{rank}</span>;
  };

  const entries = activeTab === 'winners' ? data?.top_winners : activeTab === 'roi' ? data?.top_roi : friendsData;
  const myRank = data?.my_rank;

  return (
    <>
      <style>{`
        /* ── Floating Tile Toggle ──
           Neutrales slate-Glas wie der Rest des Dark Themes, statt
           emerald-getoenter Container. Orange Trophy bleibt der Accent. */
        .lb-fab {
          position: fixed;
          left: 16px;
          bottom: 80px;
          z-index: 45;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(15, 23, 42, 0.85);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 4px 20px rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
        }
        .lb-fab:hover {
          transform: scale(1.08);
          background: rgba(30, 41, 59, 0.9);
          box-shadow: 0 6px 28px rgba(0,0,0,0.5);
        }

        @media (min-width: 768px) {
          .lb-fab { left: 20px; bottom: 24px; }
        }
        /* Auf Mobile kein Floating-Button mehr — überlappte den Content.
           Geöffnet wird dort über den Leaderboard-Eintrag im Burger-Menü. */
        @media (max-width: 767px) {
          .lb-fab { display: none; }
        }

        /* Backdrop nur auf Mobile (Bottom-Sheet). Dimmt den Hintergrund
           und schließt das Sheet bei Tap. */
        .lb-backdrop {
          position: fixed;
          inset: 0;
          z-index: 59;
          background: rgba(0, 0, 0, 0.55);
          -webkit-backdrop-filter: blur(2px);
          backdrop-filter: blur(2px);
          animation: lbFade 0.2s ease;
        }
        @media (min-width: 768px) {
          .lb-backdrop { display: none; }
        }
        @keyframes lbFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes lbSheetUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }

        /* Grabber-Bar oben am Bottom-Sheet (nur Mobile). */
        .lb-grabber { display: none; }
        @media (max-width: 767px) {
          .lb-grabber {
            display: block;
            width: 36px;
            height: 4px;
            border-radius: 999px;
            background: rgba(255, 255, 255, 0.2);
            margin: 8px auto 0;
            flex-shrink: 0;
          }
        }

        /* ── Floating Tile Card ── */
        .lb-tile {
          position: fixed;
          left: 16px;
          bottom: 80px;
          z-index: 46;
          width: 280px;
          height: 340px;
          border-radius: 16px;
          background: rgba(15, 23, 42, 0.92);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow:
            0 12px 40px rgba(0, 0, 0, 0.5),
            inset 0 1px 0 rgba(255,255,255,0.04);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: lbSlideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @media (min-width: 768px) {
          .lb-tile { left: 20px; bottom: 24px; }
        }

        /* Mobile: vollwertiges Bottom-Sheet statt schwebende Kachel.
           Volle Breite, am unteren Rand verankert, slidet hoch, deckt auch
           die BottomNav (z-50) ab und ist höher + scrollbar. */
        @media (max-width: 767px) {
          .lb-tile {
            left: 0;
            right: 0;
            bottom: 0;
            top: auto;
            width: auto;
            height: auto;
            max-height: 82vh;
            border-radius: 18px 18px 0 0;
            z-index: 60;
            animation: lbSheetUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
            padding-bottom: max(8px, env(safe-area-inset-bottom));
          }
          /* Größere, fingerfreundliche Maße im Bottom-Sheet -- die
             Default-Werte waren für die 280px-Desktop-Kachel getunt und
             wirken vollbreit zu klein. */
          .lb-head { padding: 14px 16px 8px; }
          .lb-title { font-size: 16px; }
          .lb-tabs { padding: 4px 16px 10px; gap: 6px; }
          .lb-tab { font-size: 13px; padding: 10px 4px; gap: 5px; }
          .lb-list { padding: 0 12px 8px; }
          .lb-entry { padding: 11px 10px; margin-bottom: 4px; }
          .lb-rank-icon { font-size: 18px; width: 30px; }
          .lb-rank-num { font-size: 13px; width: 30px; }
          .lb-name { font-size: 14px; margin: 0 8px; }
          .lb-metric { font-size: 13px; }
          .lb-cosmetic-badge { font-size: 10px; line-height: 18px; padding: 0 5px; }
          .lb-foot { padding: 12px 16px; }
          .lb-foot-label { font-size: 11px; }
          .lb-foot-value { font-size: 16px; }
          .lb-foot-sub { font-size: 12px; }
        }

        @keyframes lbSlideIn {
          0% { opacity: 0; transform: translateY(16px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* ── Header ── */
        .lb-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px 6px;
        }
        .lb-title {
          color: rgb(243, 244, 246);
          font-size: 14px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .lb-close {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: rgba(255,255,255,0.06);
          border: none;
          color: rgb(156, 163, 175);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
        }
        .lb-close:hover { background: rgba(255,255,255,0.12); color: rgb(243, 244, 246); }

        .lb-trophy-pulse {
          display: inline-flex;
          animation: trophyPulse 2.5s ease-in-out infinite;
        }
        @keyframes trophyPulse {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(1.12); filter: brightness(1.3) drop-shadow(0 0 4px #f97316); }
        }

        /* ── Tabs (Pill-Style, matched mit den Filter-Pillen
            auf /markets + Landing) ── */
        .lb-tabs {
          display: flex;
          gap: 4px;
          padding: 4px 14px 8px;
        }
        .lb-tab {
          flex: 1;
          min-width: 0;
          padding: 6px 4px;
          font-size: 11px;
          font-weight: 600;
          text-align: center;
          white-space: nowrap;
          border-radius: 999px;
          cursor: pointer;
          color: rgb(156, 163, 175);
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.08);
          transition: all 0.15s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }
        .lb-tab.active {
          background: rgba(59, 130, 246, 0.3);
          border-color: rgba(59, 130, 246, 0.4);
          color: rgb(255, 255, 255);
        }
        .lb-tab:hover:not(.active) {
          background: rgba(30, 41, 59, 0.6);
          color: rgb(229, 231, 235);
        }

        /* ── List ── */
        .lb-list {
          flex: 1;
          overflow-y: auto;
          padding: 0 10px 6px;
          position: relative;
          min-height: 0;
        }
        .lb-list::-webkit-scrollbar { width: 4px; }
        .lb-list::-webkit-scrollbar-track { background: transparent; }
        .lb-list::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }

        .lb-entry {
          display: flex;
          align-items: center;
          padding: 7px 8px;
          border-radius: 8px;
          margin-bottom: 2px;
          transition: background 0.15s;
          position: relative;
          overflow: hidden;
        }
        .lb-entry:hover { background: rgba(255,255,255,0.04); }
        .lb-entry.self {
          background: rgba(59, 130, 246, 0.12);
          border: 1px solid rgba(59, 130, 246, 0.25);
        }
        .lb-entry.rank-1 {
          background: linear-gradient(90deg, rgba(249, 115, 22, 0.08) 0%, transparent 100%);
        }

        .lb-rank-icon { font-size: 16px; width: 26px; text-align: center; flex-shrink: 0; }
        .lb-rank-num {
          font-size: 11px;
          color: rgb(156, 163, 175);
          width: 26px;
          text-align: center;
          flex-shrink: 0;
          font-weight: 700;
        }
        .lb-name {
          flex: 1;
          font-size: 12px;
          color: rgb(229, 231, 235);
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin: 0 6px;
          display: flex;
          align-items: center;
          gap: 3px;
        }
        .lb-cosmetic-badge {
          font-size: 9px;
          padding: 0 4px;
          border-radius: 3px;
          font-weight: 700;
          line-height: 16px;
          flex-shrink: 0;
        }
        .lb-badge-bronze { background: rgba(180,83,9,0.2); color: #fbbf24; }
        .lb-badge-silver { background: rgba(156,163,175,0.2); color: #d1d5db; }
        .lb-badge-gold { background: rgba(234,179,8,0.2); color: #facc15; }
        .lb-pro { background: rgba(139,92,246,0.25); color: #c4b5fd; }
        .lb-name-bronze { color: #fbbf24; }
        .lb-name-silver { color: #d1d5db; }
        .lb-name-gold { color: #facc15; }
        .lb-metric {
          font-size: 11px;
          font-weight: 700;
          color: rgb(52, 211, 153);
          white-space: nowrap;
        }
        .lb-entry.rank-1 .lb-name { color: #fbbf24; }

        /* Gold shimmer */
        .lb-entry.rank-1::before {
          content: '';
          position: absolute;
          top: 0; left: -100%;
          width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(249, 115, 22, 0.1), transparent);
          animation: goldShimmer 3s ease-in-out infinite;
        }
        @keyframes goldShimmer {
          0% { left: -100%; }
          100% { left: 200%; }
        }

        /* ── My Rank Footer ── */
        .lb-foot {
          padding: 8px 12px;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(255, 255, 255, 0.02);
        }
        .lb-foot-label {
          font-size: 9px;
          color: rgb(156, 163, 175);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 700;
        }
        .lb-foot-value {
          font-size: 13px;
          color: rgb(243, 244, 246);
          font-weight: 700;
          margin-top: 1px;
        }
        .lb-foot-sub {
          font-size: 10px;
          color: rgb(156, 163, 175);
          margin-top: 1px;
        }

        .lb-empty {
          padding: 20px 12px;
          text-align: center;
          color: rgb(156, 163, 175);
          font-size: 12px;
        }

        /* Confetti */
        .lb-confetti {
          position: absolute;
          font-size: 20px;
          animation: confettiFly 2s ease-out forwards;
          z-index: 47;
          pointer-events: none;
        }
        @keyframes confettiFly {
          0% { opacity: 1; transform: translateY(0) rotate(0deg); }
          50% { opacity: 1; }
          100% { opacity: 0; transform: translateY(-200px) rotate(360deg) translateX(30px); }
        }
      `}</style>

      {/* Collapsed: floating button */}
      {!open && (
        <div className="lb-fab" onClick={() => setOpen(true)} data-testid="leaderboard-toggle">
          <Trophy size={20} color="#f97316" />
        </div>
      )}

      {/* Open: floating tile (Desktop) / Bottom-Sheet (Mobile) */}
      {open && (
        <>
        <div className="lb-backdrop" onClick={() => setOpen(false)} />
        <div className="lb-tile" data-testid="leaderboard-panel">
          <div className="lb-grabber" />
          {/* Header */}
          <div className="lb-head">
            <div className="lb-title">
              <span className="lb-trophy-pulse"><Trophy size={16} color="#f97316" /></span>
              Leaderboard
            </div>
            <button className="lb-close" onClick={() => setOpen(false)} data-testid="leaderboard-close">
              <X size={14} />
            </button>
          </div>

          {/* Tabs */}
          <div className="lb-tabs">
            <button
              className={`lb-tab ${activeTab === 'winners' ? 'active' : ''}`}
              onClick={() => setActiveTab('winners')}
              data-testid="leaderboard-tab-winners"
            >
              <Trophy size={11} /> Top Gewinner
            </button>
            <button
              className={`lb-tab ${activeTab === 'roi' ? 'active' : ''}`}
              onClick={() => setActiveTab('roi')}
              data-testid="leaderboard-tab-roi"
            >
              <TrendingUp size={11} /> Beste ROI
            </button>
            {user && (
              <button
                className={`lb-tab ${activeTab === 'friends' ? 'active' : ''}`}
                onClick={() => setActiveTab('friends')}
                data-testid="leaderboard-tab-friends"
              >
                <Users size={11} /> Freunde
              </button>
            )}
          </div>

          {/* Entries */}
          <div className="lb-list" data-testid="leaderboard-list">
            {confetti && (
              <span className="lb-confetti" style={{ left: `${Math.random() * 200 + 20}px`, bottom: 0 }}>
                {confetti}
              </span>
            )}

            {!entries || entries.length === 0 ? (
              <div className="lb-empty">
                {activeTab === 'roi'
                  ? 'Mind. 5 Wetten nötig für ROI-Ranking'
                  : activeTab === 'friends'
                  ? 'Noch keine Freunde oder keine Wetten'
                  : 'Noch keine Teilnehmer'}
              </div>
            ) : (
              entries.map((entry) => {
                const isSelf = user && (entry.user_id === user.id || entry.is_self);
                const isRank1 = entry.rank === 1;
                const cos = entry.cosmetics || {};
                const nameColorClass = cos.name_color ? `lb-name-${cos.name_color.tier || 'bronze'}` : '';
                return (
                  <div
                    key={entry.user_id}
                    className={`lb-entry ${isSelf ? 'self' : ''} ${isRank1 ? 'rank-1' : ''}`}
                    data-testid={`leaderboard-entry-${entry.rank}`}
                  >
                    {getRankIcon(entry.rank)}
                    <span className={`lb-name ${nameColorClass}`}>
                      <span style={{overflow:'hidden',textOverflow:'ellipsis'}}>{entry.username}</span>
                      {cos.pro_badge && <span className="lb-cosmetic-badge lb-pro">PRO</span>}
                      {cos.custom_title && <span className={`lb-cosmetic-badge lb-badge-${cos.custom_title.tier || 'silver'}`}>{cos.custom_title.item_name}</span>}
                    </span>
                    <span className="lb-metric">
                      {activeTab === 'winners'
                        ? `${entry.total_won.toFixed(0)} VC`
                        : activeTab === 'roi'
                        ? `${entry.roi_percent > 0 ? '+' : ''}${entry.roi_percent}%`
                        : `${entry.total_won.toFixed(0)} VC`}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Own rank */}
          {user && myRank && (
            <div className="lb-foot" data-testid="leaderboard-my-rank">
              <div className="lb-foot-label">Dein Rang</div>
              <div className="lb-foot-value">
                {activeTab === 'winners'
                  ? (myRank.winner_rank
                      ? `#${myRank.winner_rank} von ${myRank.winner_total}`
                      : 'Keine Wetten')
                  : (myRank.roi_rank
                      ? `#${myRank.roi_rank} von ${myRank.roi_total}`
                      : myRank.total_bets < 5
                        ? `${5 - myRank.total_bets} Wetten bis ROI`
                        : 'Nicht im Ranking')}
              </div>
              {myRank.roi_percent !== null && myRank.roi_percent !== undefined && (
                <div className="lb-foot-sub">
                  ROI: {myRank.roi_percent > 0 ? '+' : ''}{myRank.roi_percent}% | {myRank.total_won.toFixed(0)} VC
                </div>
              )}
              {!myRank.leaderboard_visible && (
                <div className="lb-foot-sub" style={{ color: '#f59e0b' }}>
                  Nicht sichtbar — in Settings aktivieren
                </div>
              )}
            </div>
          )}
        </div>
        </>
      )}
    </>
  );
};

export default LeaderboardPanel;
